import React, { useEffect, useState } from "react";
import { CheckCircle2, Download, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ReplaceSigned from "./ReplaceSigned";
import { reApi, errMsg, fmtDateTime, saveBlob } from "@/lib/residentApi";
import { ErrorLine } from "./ui";

const fileName = (t, who) => `${[t, who].filter(Boolean).join(" - ").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "-") || "document"}-signed.pdf`;

/** Signed agreements from the Document Engine, read-only for every staff member. */
export default function SignedDocs({ resident, onChanged }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [canReplace, setCanReplace] = useState(false);
  const [replacing, setReplacing] = useState(null);
  const load = () => reApi.get("/documents/signed", { resident_id: resident.id }).then((d) => { setRows(d.signed); setCanReplace(!!d.can_replace); }).catch((e) => (e?.response?.status === 403 ? setHidden(true) : setError(errMsg(e))));
  useEffect(() => { load(); }, [resident.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async (q) => {
    setBusy(q.id); setError(null);
    try { saveBlob(await reApi.blob(`/documents/signed/${q.id}/file`), fileName(q.title, resident.name || resident.full_name)); }
    catch (e) { setError(e?.response?.data instanceof Blob ? "Couldn't download this document" : errMsg(e)); }
    finally { setBusy(null); }
  };
  if (hidden) return null;
  return (
    <section data-testid="signed-docs" className="space-y-2">
      <h4 className="font-mono text-[10px] uppercase tracking-wider text-nex-muted">Signed agreements · {rows ? rows.length : "…"}</h4>
      <ErrorLine testId="signed-docs-error">{error}</ErrorLine>
      {!rows ? (!error && <Loader2 className="h-4 w-4 animate-spin text-nex-muted" />) : !rows.length ? (
        <p data-testid="signed-docs-empty" className="text-[11px] text-nex-muted">No signed agreements yet.</p>
      ) : (
        <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">
          {rows.map((q) => (
            <li key={q.id} data-testid={`signed-doc-${q.id}`} className="px-3 py-2 flex items-center gap-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span className="truncate flex-1 min-w-0 text-nex-silver">{q.title}</span>
              <span className="text-[11px] text-nex-muted whitespace-nowrap hidden sm:inline">{q.signed_at_local || fmtDateTime(q.signed_at)}</span>
              <button type="button" onClick={() => download(q)} disabled={busy === q.id} data-testid={`signed-doc-download-${q.id}`} aria-label="Download PDF"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-nex-gold/50 px-2 py-1 text-[11px] text-nex-gold hover:bg-nex-gold/10 disabled:opacity-50">
                {busy === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}<span className="hidden sm:inline">Download PDF</span>
              </button>
              {canReplace && <button type="button" onClick={() => setReplacing(q)} data-testid={`signed-doc-replace-${q.id}`} aria-label="Replace"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-white/15 px-2 py-1 text-[11px] text-nex-muted hover:text-white hover:border-white/30">
                <RefreshCw className="h-3 w-3" /><span className="hidden sm:inline">Replace</span></button>}
            </li>
          ))}
        </ul>
      )}
      {replacing && <ReplaceSigned doc={replacing} onClose={() => setReplacing(null)}
        onDone={() => { toast.success(`Deleted. "${replacing.title}" was sent back to the resident to sign again.`); setReplacing(null); load(); onChanged?.(); }} />}
    </section>
  );
}
